"use client"

import { useState, useEffect, Fragment } from "react"
import { useAuth } from "@/hooks/useAuth"
import {
  getDiskUsage,
  getDiskUsageMetadata,
  type DiskUsageResponse,
  type DiskUsageMetadataResponse,
  type DiskUsageScope,
} from "@/services/resourceUsageService"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Bug, HardDrive } from "lucide-react"
import LoadingSpinner from "@/components/shared/LoadingSpinner"

const PAGE_TITLE = "Resource Usage (Debug)"

/**
 * Formats a byte count into a human-readable string (e.g. "1.23 GB").
 *
 * @param bytes - The number of bytes to format.
 * @returns A formatted string with appropriate unit (B, KB, MB, GB, TB).
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B"

  const units = ["B", "KB", "MB", "GB", "TB"]
  // Determine the appropriate unit by dividing by 1024 repeatedly
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  )
  const value = bytes / Math.pow(1024, exponent)

  return `${value.toFixed(2)} ${units[exponent]}`
}

/**
 * Debug interface for testing the get_disk_usage Lambda endpoint.
 *
 * This component is a developer tool — NOT the production resource usage UI.
 * It allows manually invoking the Lambda with the current user's JWT and
 * inspecting the raw API response. Only accessible in dev mode.
 *
 * On load, fetches metadata to determine available scopes and latest versions.
 */
export default function ResourceUsageDebugInterface() {
  const { user, getAccessToken } = useAuth()

  // Metadata state (loaded on mount)
  const [metadata, setMetadata] = useState<DiskUsageMetadataResponse | null>(null)
  const [metadataLoading, setMetadataLoading] = useState<boolean>(true)
  const [metadataError, setMetadataError] = useState<string | null>(null)

  // Form state
  const [scope, setScope] = useState<DiskUsageScope | "">("")
  const [path, setPath] = useState<string>("")

  // Request state
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [result, setResult] = useState<DiskUsageResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  /**
   * Fetch metadata on component mount to determine available scopes
   * and latest versions for the current user.
   */
  useEffect(() => {
    async function fetchMetadata(): Promise<void> {
      try {
        const accessToken = await getAccessToken()
        const data = await getDiskUsageMetadata(accessToken)
        setMetadata(data)

        // Auto-select the first available scope
        if (data.scopes_available.length > 0) {
          setScope(data.scopes_available[0])
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to load metadata"
        setMetadataError(message)
      } finally {
        setMetadataLoading(false)
      }
    }

    fetchMetadata()
  }, [getAccessToken])

  /**
   * Handles the "Test Lambda" button click.
   * Fetches disk usage data from the Lambda using the current user's token,
   * scope, and the latest version from metadata.
   */
  async function handleFetchDiskUsage(): Promise<void> {
    if (!scope || !metadata) return

    const version = metadata.latest[scope]
    if (!version) {
      setError(`No version available for scope "${scope}"`)
      return
    }

    setIsLoading(true)
    setError(null)
    setResult(null)

    try {
      const accessToken = await getAccessToken()

      const response = await getDiskUsage(
        {
          scope,
          version,
          path: path || undefined,
        },
        accessToken
      )

      setResult(response)
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "An unexpected error occurred"
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
    <title>{PAGE_TITLE} - MAP Apps Platform</title>
    <div className="flex flex-1 flex-col gap-6 p-6 overflow-auto">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <Bug className="h-6 w-6 text-orange-500" />
        <div>
          <h1>{PAGE_TITLE}</h1>
          <p className="text-muted-foreground mt-1">
            Developer tool for testing the <code>get_disk_usage</code> Lambda with your current auth
            context.
          </p>
        </div>
      </div>

      {/* User context card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Current User Context</CardTitle>
          <CardDescription>
            The Lambda derives the user identity from your JWT claims.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="font-medium text-muted-foreground">Name</dt>
            <dd>{user?.name ?? "Local Developer"}</dd>
            <dt className="font-medium text-muted-foreground">Email</dt>
            <dd>{user?.email ?? "N/A (local dev mode)"}</dd>
          </dl>
        </CardContent>
      </Card>

      {/* Metadata card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Scan Metadata</CardTitle>
          <CardDescription>
            Latest versions and available scopes from <code>get_disk_usage_metadata</code>.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {metadataLoading && <LoadingSpinner loadingContent="Loading metadata..." />}
          {metadataError && <p className="text-sm text-destructive">{metadataError}</p>}
          {metadata && (
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Latest versions</p>
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm font-mono">
                  {Object.entries(metadata.latest).map(([s, v]) => (
                    <Fragment key={s}>
                      <dt>{s}</dt>
                      <dd>{v ?? <span className="text-muted-foreground italic">none</span>}</dd>
                    </Fragment>
                  ))}
                </dl>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Scopes available for you
                </p>
                {metadata.scopes_available.length > 0 ? (
                  <ul className="text-sm font-mono list-disc list-inside">
                    {metadata.scopes_available.map(s => (
                      <li key={s}>{s}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground italic">No scopes available</p>
                )}
              </div>
              {metadata.users_available && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">
                    Users available (admin)
                  </p>
                  <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm font-mono">
                    {Object.entries(metadata.users_available).map(([s, users]) => (
                      <Fragment key={s}>
                        <dt>{s}</dt>
                        <dd>
                          {users.length > 0 ? (
                            users.join(", ")
                          ) : (
                            <span className="text-muted-foreground italic">none</span>
                          )}
                        </dd>
                      </Fragment>
                    ))}
                  </dl>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Query controls */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Test Parameters</CardTitle>
          <CardDescription>
            Select a scope and optionally specify a path, then invoke the Lambda. Version is
            automatically set from metadata.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Scope selector — only shows available scopes */}
          <div className="space-y-2">
            <label htmlFor="scope-select" className="text-sm font-medium leading-none">
              Scope
            </label>
            <Select
              value={scope}
              onValueChange={(value: string) => setScope(value as DiskUsageScope)}
              disabled={!metadata || metadata.scopes_available.length === 0}
            >
              <SelectTrigger id="scope-select" className="w-full max-w-xs">
                <SelectValue placeholder="Select a scope" />
              </SelectTrigger>
              <SelectContent>
                {metadata?.scopes_available.map(s => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {scope && metadata?.latest[scope] && (
              <p className="text-xs text-muted-foreground">
                Version: <span className="font-mono">{metadata.latest[scope]}</span>
              </p>
            )}
          </div>

          {/* Path input */}
          <div className="space-y-2">
            <label htmlFor="path-input" className="text-sm font-medium leading-none">
              Path (optional)
            </label>
            <input
              id="path-input"
              type="text"
              value={path}
              onChange={e => setPath(e.target.value)}
              placeholder="/ (root)"
              className="flex h-10 w-full max-w-xs rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          {/* Fetch button */}
          <Button
            onClick={handleFetchDiskUsage}
            disabled={isLoading || !scope || !metadata}
            className="mt-2"
          >
            {isLoading ? (
              <LoadingSpinner loadingContent="Calling Lambda..." />
            ) : (
              <>
                <HardDrive className="mr-2 h-4 w-4" />
                Test get_disk_usage
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Error display */}
      {error && (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-lg text-destructive">Lambda Error</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Results display */}
      {result && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <HardDrive className="h-5 w-5" />
              Response — {result.path}
            </CardTitle>
            <CardDescription>
              Total disk size: {formatBytes(result.cumdsize)} &middot; {result.items} items
              {result.parentPath && (
                <>
                  {" "}
                  &middot; Parent:{" "}
                  <button
                    onClick={() => setPath(result.parentPath ?? "")}
                    className="underline text-primary hover:text-primary/80"
                  >
                    {result.parentPath}
                  </button>
                </>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {result.contents.length === 0 ? (
              <p className="text-sm text-muted-foreground">No items found in this directory.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-4 font-medium">Name</th>
                      <th className="text-left py-2 pr-4 font-medium">Type</th>
                      <th className="text-right py-2 pr-4 font-medium">Disk Size</th>
                      <th className="text-right py-2 font-medium">Items</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...result.contents]
                      .sort((a, b) => {
                        const sizeA = a.isDirectory ? (a.cumdsize ?? 0) : a.dsize
                        const sizeB = b.isDirectory ? (b.cumdsize ?? 0) : b.dsize
                        return sizeB - sizeA
                      })
                      .map((item, index) => (
                        <tr key={index} className="border-b last:border-0">
                          <td className="py-2 pr-4">
                            {item.isDirectory ? (
                              <button
                                onClick={() =>
                                  setPath(
                                    result.path === "/"
                                      ? `/${item.name}`
                                      : `${result.path}/${item.name}`
                                  )
                                }
                                className="text-primary hover:underline font-medium"
                              >
                                📁 {item.name}
                              </button>
                            ) : (
                              <span>📄 {item.name}</span>
                            )}
                          </td>
                          <td className="py-2 pr-4 text-muted-foreground">{item.type}</td>
                          <td className="py-2 pr-4 text-right font-mono">
                            {formatBytes(item.isDirectory ? (item.cumdsize ?? 0) : item.dsize)}
                          </td>
                          <td className="py-2 text-right font-mono">{item.items ?? "—"}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
    </>
  )
}
